import React from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';

export interface LocacoesAnalyticsChartsData {
  mensal?: Array<{ name: string; novas: number; canceladas: number }>;
  mensalAnos?: number[];
  mensalComparativo?: Array<{ mes: number; name: string } & Record<string, number | string>>;
  diario: Array<{ name: string; novas: number; canceladas: number }>;
  topModeloMensal?: Array<{
    name: string;
    marca: string;
    modelo: string;
    quantidade: number;
  }>;
  topModeloAnos?: number[];
  topModeloComparativo?: Array<{ mes: number; name: string } & Record<string, number | string>>;
  topPlacas: Array<{
    placa: string;
    marca: string;
    modelo: string;
    empresa?: string;
    idEmpresa?: string;
    quantidade: number;
  }>;
}

/** Criadas (sólido) e canceladas (tracejado) por ano — tons distintos desde 2025. */
const YEAR_PALETTE: Array<{ criadas: string; canceladas: string }> = [
  { criadas: 'hsl(222 47% 18%)', canceladas: '#ca8a04' },
  { criadas: 'hsl(199 89% 38%)', canceladas: '#ea580c' },
  { criadas: 'hsl(160 60% 32%)', canceladas: '#a16207' },
  { criadas: 'hsl(280 45% 40%)', canceladas: '#c2410c' },
];

const COLOR_NOVAS = 'hsl(222 47% 18%)';
const COLOR_CANCEL = '#ca8a04';

export const DiretoriaLocacoesCharts: React.FC<{ data: LocacoesAnalyticsChartsData | undefined }> = ({
  data,
}) => {
  const anos = data?.mensalAnos ?? [];
  const mensalComparativo = data?.mensalComparativo ?? [];
  const hasMensalComparativo = mensalComparativo.length === 12 && anos.length > 0;

  const topModeloAnos = data?.topModeloAnos ?? anos;
  const topModeloComparativo = data?.topModeloComparativo ?? [];
  const hasTopModeloComparativo = topModeloComparativo.length === 12 && topModeloAnos.length > 0;

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      <Card className="border-border/80">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Locações mês a mês</CardTitle>
          <p className="text-xs text-muted-foreground">
            Jan–Dez · séries por ano desde 2025 (empresa selecionada)
          </p>
        </CardHeader>
        <CardContent className="h-72 pt-0">
          {hasMensalComparativo ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={mensalComparativo}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {anos.map((ano, i) => {
                  const palette = YEAR_PALETTE[i % YEAR_PALETTE.length]!;
                  return (
                    <React.Fragment key={ano}>
                      <Line
                        type="monotone"
                        dataKey={`novas_${ano}`}
                        name={`Criadas ${ano}`}
                        stroke={palette.criadas}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                      />
                      <Line
                        type="monotone"
                        dataKey={`canceladas_${ano}`}
                        name={`Canceladas ${ano}`}
                        stroke={palette.canceladas}
                        strokeWidth={2}
                        strokeDasharray="5 4"
                        dot={{ r: 3 }}
                      />
                    </React.Fragment>
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="flex h-full items-center justify-center text-sm text-muted-foreground">
              Sem dados no período
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="border-border/80">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Locações dia a dia</CardTitle>
          <p className="text-xs text-muted-foreground">Criadas e cancelamentos no intervalo</p>
        </CardHeader>
        <CardContent className="h-64 pt-0">
          {(data?.diario?.length ?? 0) > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data!.diario}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="novas" name="Criadas" stroke={COLOR_NOVAS} strokeWidth={2} dot={false} />
                <Line
                  type="monotone"
                  dataKey="canceladas"
                  name="Canceladas"
                  stroke={COLOR_CANCEL}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="flex h-full items-center justify-center text-sm text-muted-foreground">
              Sem dados no período
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="border-border/80">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Modelo que mais alugou (mês a mês)</CardTitle>
          <p className="text-xs text-muted-foreground">
            Jan–Dez · quantidade do top modelo por ano desde 2025
          </p>
        </CardHeader>
        <CardContent className="h-72 pt-0">
          {hasTopModeloComparativo ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topModeloComparativo} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(v: number, name: string, item) => {
                    const ano = String(name);
                    const modelo = String(
                      (item?.payload as Record<string, unknown>)?.[`modelo_${ano}`] ?? ''
                    ).trim();
                    return [modelo ? `${v} · ${modelo}` : String(v), `Top ${ano}`];
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {topModeloAnos.map((ano, i) => {
                  const palette = YEAR_PALETTE[i % YEAR_PALETTE.length]!;
                  return (
                    <Bar
                      key={ano}
                      dataKey={`qtd_${ano}`}
                      name={`${ano}`}
                      fill={palette.criadas}
                      radius={[4, 4, 0, 0]}
                      maxBarSize={28}
                    />
                  );
                })}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="flex h-full items-center justify-center text-sm text-muted-foreground">
              Sem dados no período
            </p>
          )}
        </CardContent>
      </Card>

      <Card className="border-border/80">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Placas com mais locações</CardTitle>
          <p className="text-xs text-muted-foreground">Histórico geral (todas as locações importadas)</p>
        </CardHeader>
        <CardContent className="pt-0">
          {(data?.topPlacas?.length ?? 0) > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs text-muted-foreground">
                    <th className="py-2 pr-2 font-medium">#</th>
                    <th className="py-2 pr-2 font-medium">Placa</th>
                    <th className="py-2 pr-2 font-medium">Modelo</th>
                    <th className="py-2 pr-2 font-medium">Empresa</th>
                    <th className="py-2 text-right font-medium">Locações</th>
                  </tr>
                </thead>
                <tbody>
                  {data!.topPlacas.map((row, i) => (
                    <tr key={`${row.placa}-${row.empresa ?? ''}-${i}`} className="border-b border-border/60">
                      <td className="py-2 pr-2 tabular-nums text-muted-foreground">{i + 1}</td>
                      <td className="py-2 pr-2 font-medium">{row.placa}</td>
                      <td className="py-2 pr-2 text-muted-foreground">
                        {[row.marca, row.modelo].filter(Boolean).join(' ') || '—'}
                      </td>
                      <td className="py-2 pr-2 text-muted-foreground">{row.empresa || '—'}</td>
                      <td className="py-2 text-right tabular-nums font-semibold">{row.quantidade}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground">Sem dados no período</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
